import { Group, ShaderMaterial, AdditiveBlending, PlaneGeometry, Mesh } from 'three';
import gsap from 'gsap';

export default class BenMarcDigitsWall {
    constructor(experience) {
        this.experience = experience;

        this.digits = [
            experience.textures.digit0.map,
            experience.textures.digit1.map
        ];

        this.spriteWidthPx = 10;
        this.spriteHeightPx = 16;

        this.spriteWidth = this.spriteWidthPx * this.experience.sizes.pixelSize;
        this.spriteHeight = this.spriteHeightPx * this.experience.sizes.pixelSize;

        this.spritesRowsCount = 7;
        this.spritesColumnsCount = 23;
        this.animationHistory = [];
        this.historyLimit = 30;
        this.sprites = [];

        this.isNarrowScreen = window.innerWidth < 1024;

        this.init();
    }

    init() {
        this.initWall();
        this.calculateSpacing();
        this.initShader();
        this.createSprites();
        this.fadeInSprites();
    }

    initWall() {
        this.wall = new Group();
        this.wall.position.set(0, 0, 0);
        this.experience.scene.add(this.wall);
    }

    calculateSpacing() {
        this.horizontalSpacing = (this.experience.sizes.emWidth - this.spriteWidth * this.spritesColumnsCount) / (this.spritesColumnsCount + 1);
        this.verticalSpacing = (this.experience.sizes.emHeight - this.spriteHeight * this.spritesRowsCount) / (this.spritesRowsCount + 1);
    }

    initShader() {
        this.shaderMaterial = new ShaderMaterial({
            transparent: true,
            blending: AdditiveBlending,
            uniforms: {
                texture0: { value: this.digits[0] },
                texture1: { value: this.digits[1] },
                mixRatio: { value: 0.0 },
                opacity: { value: 0.0 },
                scale: { value: 0.0 },
                visible: { value: true }
            },
            vertexShader: `
                uniform float scale;
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec3 scaled = position * scale;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(scaled, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D texture0;
                uniform sampler2D texture1;
                uniform float mixRatio;
                uniform float opacity;
                uniform float visible;
                varying vec2 vUv;
                void main() {
                    if(visible == 0.0) {
                        discard;
                    }
                    vec4 color0 = texture2D(texture0, vUv);
                    vec4 color1 = texture2D(texture1, vUv);
                    vec4 mixedColor = mix(color0, color1, mixRatio);
                    gl_FragColor = vec4(mixedColor.rgb, mixedColor.a * opacity);
                    #include <colorspace_fragment>
                }
            `
        });
    }

    createSprites() {
        const geometry = new PlaneGeometry(this.spriteWidth, this.spriteHeight);

        for (let row = 0; row < this.spritesRowsCount; row++) {
            for (let col = 0; col < this.spritesColumnsCount; col++) {
                // const material = this.colorMaterial.clone();
                const material = this.shaderMaterial.clone();

                material.uniforms.visible.value = this.isNarrowScreen && col % 2 === 0 ? 0 : 1;

                const mesh = new Mesh(geometry, material);

                const x = this.horizontalSpacing + col * (this.spriteWidth + this.horizontalSpacing) - this.experience.sizes.emWidth / 2;
                const y = this.experience.sizes.emHeight / 2 - (this.verticalSpacing + row * (this.spriteHeight + this.verticalSpacing));


                mesh.position.x = x;//col * (this.spriteWidth + 0.1) - (this.spritesColumnsCount * this.spriteWidth) / 2;
                mesh.position.y = y;//-row * (this.spriteHeight + 0.1) + (this.spritesRowsCount * this.spriteHeight) / 2;

                this.wall.add(mesh);

                this.sprites.push({
                    mesh,
                    material,
                    row,
                    col,
                    type: Math.round(Math.random()),
                    animating: false
                });
            }
        }
    }

    animateRandomSprite() {
        const availableSprites = this.sprites.filter(sprite =>
            !sprite.animating && !this.animationHistory.includes(sprite)
        );

        if (availableSprites.length === 0) return;

        const randomIndex = Math.floor(Math.random() * availableSprites.length);
        const sprite = availableSprites[randomIndex];

        sprite.animating = true;
        const newType = sprite.type === 0 ? 1 : 0;

        gsap.to(sprite.material.uniforms.mixRatio, {
            value: newType,
            duration: 0.5,
            onComplete: () => {
                sprite.type = newType;
                sprite.animating = false;
                this.animationHistory.push(sprite);
                if (this.animationHistory.length > this.historyLimit) {
                    this.animationHistory.shift();
                }
            }
        });
    }

    update() {
        this.animateRandomSprite();
    }

    fadeInSprites() {
        this.sprites.forEach((sprite, index) => {
            gsap.to(sprite.material.uniforms.opacity, {
                value: 1.0,
                duration: 1,
                delay: sprite.col * 0.1,
            });
            gsap.to(sprite.material.uniforms.scale, {
                value: 1.0,
                duration: 1,
                delay: sprite.col * 0.1,
            });
        });
    }

    resize() {
        this.calculateSpacing();

        this.isNarrowScreen = window.innerWidth < 1024;

        this.sprites.forEach((sprite, index) => {
            const x = this.horizontalSpacing + sprite.col * (this.spriteWidth + this.horizontalSpacing) - this.experience.sizes.emWidth / 2;
            const y = this.experience.sizes.emHeight / 2 - (this.verticalSpacing + sprite.row * (this.spriteHeight + this.verticalSpacing));

            sprite.mesh.material.uniforms.visible.value = this.isNarrowScreen && sprite.col % 2 === 0 ? 0 : 1;

            sprite.mesh.position.x = x;
            sprite.mesh.position.y = y;
        });
    }
}
